type Result<T, E = string> =
  | { type: 'success'; data: T }
  | { type: 'error'; data: E }

const isHeicFile = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  /\.hei[cf]$/i.test(file.name)

const convertHeicToJpeg = async (file: File): Promise<File> => {
  // heic-to minimized is 2.5mb. dynamic import to only affect the load if someone actually uploads one
  const { heicTo } = await import('heic-to')
  const blob = await heicTo({
    blob: file,
    type: 'image/jpeg',
    quality: 0.9,
  })
  return new File([blob], file.name.replace(/\.hei[cf]$/i, '.jpg'), {
    type: 'image/jpeg',
  })
}

// 1. get signed url 2. post to it 3. upload to the redirect (location response header)
// https://stackoverflow.com/questions/43513928/library-to-upload-to-google-cloud-storage-with-a-signed-url
const getNewSignedURL = async (
  file: File,
): Promise<Result<{ upload_url: string; gcs_location: string }>> => {
  const response = await fetch('/api/getSignedUrl', {
    method: 'POST',
    body: JSON.stringify({
      contentType: file.type,
    }),
  })

  if (!response.ok) {
    return { type: 'error', data: 'failed to get signed url' }
  }

  const json = await response.json()
  if (!json.success) {
    return { type: 'error', data: 'failed to get signed url from body' }
  }

  const part_2 = await fetch(json.signed_url, {
    method: 'POST',
    headers: {
      'x-goog-resumable': 'start',
      'Access-Control-Expose-Headers': '*',
      'Content-Type': file.type,
    },
  })

  if (part_2.status !== 201) {
    return { type: 'error', data: 'failed to upload media #3' }
  }

  const location = part_2.headers.get('location')
  if (!location) {
    return { type: 'error', data: 'failed to upload media #4' }
  }

  return {
    type: 'success',
    data: { upload_url: location, gcs_location: json.gcs_location },
  }
}

type PerformGCSUploadOpts = {
  file: File
  onProgress?: (progress: number) => void
  maxFileSize?: number
}

export const performGCSUpload = async (
  opts: PerformGCSUploadOpts,
): Promise<Result<string>> => {
  if (opts.maxFileSize && opts.file.size >= opts.maxFileSize) {
    return {
      type: 'error',
      data:
        'File is too big. Max file size is ' +
        Math.round(opts.maxFileSize / (1024 * 1024)) +
        'MB',
    }
  }

  opts.onProgress?.(0.01)

  const file = isHeicFile(opts.file)
    ? await convertHeicToJpeg(opts.file)
    : opts.file

  const gcs = await getNewSignedURL(file)
  if (gcs.type === 'error') {
    opts.onProgress?.(1)
    return gcs
  }

  opts.onProgress?.(0.02)

  const xhr = new XMLHttpRequest()

  // Use xhr so we can calculate and report the progress
  return new Promise<Result<string>>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && opts.onProgress) {
        // Check that both `loaded` and `total` are finite numbers
        const loaded = isFinite(event.loaded) ? event.loaded : 0
        const total = isFinite(event.total) && event.total > 0 ? event.total : 1 // prevent division by zero

        const progress = loaded / total
        opts.onProgress(progress)
      }
    })

    xhr.addEventListener('load', () => {
      opts.onProgress?.(1)
      if (xhr.status === 200) {
        resolve({ type: 'success', data: gcs.data.gcs_location })
      } else {
        reject({ type: 'error', data: 'failed to upload media #5' })
      }
    })

    xhr.addEventListener('error', () => {
      reject({
        type: 'error',
        data: 'Network error occurred during the upload.',
      })
    })

    xhr.open('PUT', gcs.data.upload_url, true)
    xhr.send(file)
  })
}
